'use client'
import { useState, useEffect } from 'react'
import debounce from 'lodash/debounce'
import {
  TAILWIND_LG_BREAKPOINT,
  TAILWIND_MD_BREAKPOINT,
  TAILWIND_SM_BREAKPOINT,
  TAILWIND_XS_BREAKPOINT,
} from '@constants'
import { Breakpoint } from '@types'

const breakpointsOrder = [
  Breakpoint.XS,
  Breakpoint.SM,
  Breakpoint.MD,
  Breakpoint.LG,
  Breakpoint.XL,
]

export const useCurrentBreakpoint = (): {
  currentBreakpoint: Breakpoint
  isGreaterThan: (breakpoint: Breakpoint) => boolean
  isLessThan: (breakpoint: Breakpoint) => boolean
  isMobile: boolean
} => {
  // Default to desktop width so SSR/initial render assumes desktop layout.
  // This prevents a flash of mobile nav on desktop devices.
  const [innerWidth, setInnerWidth] = useState(1024)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    // Only update after client mount
    setIsMounted(true)
    setInnerWidth(window.innerWidth)

    const debouncedSetInnerWidth = debounce(() => {
      setInnerWidth(window.innerWidth)
    }, 100)

    window.addEventListener('resize', debouncedSetInnerWidth, { passive: true })

    return () => {
      window.removeEventListener('resize', debouncedSetInnerWidth)
      debouncedSetInnerWidth.cancel()
    }
  }, [])

  // Use 1024 (desktop) during SSR and initial client render to prevent hydration mismatch
  const width = isMounted ? innerWidth : 1024

  if (width <= TAILWIND_XS_BREAKPOINT) {
    return {
      currentBreakpoint: Breakpoint.XS,
      isMobile: true,
      isGreaterThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) > 0,
      isLessThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) === 0,
    }
  }

  if (width > TAILWIND_XS_BREAKPOINT && width <= TAILWIND_SM_BREAKPOINT) {
    return {
      currentBreakpoint: Breakpoint.SM,
      isMobile: true,
      isGreaterThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) > 1,
      isLessThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) <= 1,
    }
  }

  if (width > TAILWIND_SM_BREAKPOINT && width <= TAILWIND_MD_BREAKPOINT) {
    return {
      currentBreakpoint: Breakpoint.MD,
      isMobile: false,
      isGreaterThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) > 2,
      isLessThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) <= 2,
    }
  }

  if (width > TAILWIND_MD_BREAKPOINT && width <= TAILWIND_LG_BREAKPOINT) {
    return {
      currentBreakpoint: Breakpoint.LG,
      isMobile: false,
      isGreaterThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) > 3,
      isLessThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) <= 3,
    }
  }

  if (width > TAILWIND_LG_BREAKPOINT) {
    return {
      currentBreakpoint: Breakpoint.XL,
      isMobile: false,
      isGreaterThan: () => false,
      isLessThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) <= 4,
    }
  }

  return {
    currentBreakpoint: Breakpoint.XS,
    isMobile: true,
    isGreaterThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) > 0,
    isLessThan: (breakpoint: Breakpoint) => breakpointsOrder.indexOf(breakpoint) === 0,
  }
}
